import React, {Component} from 'react';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import Auth from '../auth/auth';

/**
 * "Effect if approved" — mockup screen S5 (docs/mockups/access-control-mockups.html).
 *
 * An approver is asked to widen access to a table. **How far** is the fact the decision turns on,
 * and until now it existed only in `scripts/access/classification-visibility.js` — a CLI run out of
 * band. The person deciding could not see it.
 *
 * A COMFORTABLE NUMBER IS AMBIGUOUS, which is the lesson that script opens with and it matters more
 * here, because this one is shown to someone about to act: "0 people gain visibility" is what a
 * correctly-scoped catalogue looks like AND what a broken measurement looks like. So a guarded
 * result renders as **"not measured"** with the reason, never as a reassuring zero.
 *
 * ponytail: self-contained (own axios + local state), matching ClassificationPanel next door. The
 * sheet it renders in is not connect()ed.
 */

const API = process.env.REACT_APP_API_URL || '';

/** Where a declassified object becomes visible — the mockup lists these explicitly. */
const APPEARS_IN = 'Search · lists · lineage · dependency graphs · ownership & PII reports';

class DeclassificationEffect extends Component {
  state = {effect: null, error: null, loading: false};

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prev) {
    if (prev.requestId !== this.props.requestId) this.load();
  }

  load() {
    // A REQUEST id, not an object id (slice 4b-3 §3.4). The endpoint derives the target entity
    // from the change-request row, which is what removes the arbitrary-object probe the old
    // :objectId form allowed — and it is what lets a SCHEMA reclassification render at all (§3.5).
    const {requestId} = this.props;
    if (!requestId) return;
    this.setState({loading: true, error: null});
    axios.get(`${API}/access/declassification-effect/${requestId}`, {
      headers: {'Accept': 'application/json', 'x-access-token': new Auth().getAccessToken()},
    })
        .then((res) => this.setState({effect: res.data, loading: false}))
        .catch((err) => this.setState({
          loading: false,
          error: (err.response && err.response.data && err.response.data.error) || 'could not load',
        }));
  }

  render() {
    const {effect, error, loading} = this.state;
    if (loading) return <div className="effect-panel-loading">Measuring…</div>;

    // An error is NOT "no effect". Saying nothing here would leave the approver believing the
    // change was weighed when it was not.
    if (error) {
      return (
        <Alert severity="warning" data-testid="cr-effect-error">
          The visibility impact could not be measured ({error}). Approve only if you can establish
          it another way.
        </Alert>
      );
    }
    if (!effect) return null;

    const blocking = (effect.guards || []).filter((g) => g.code.startsWith('POPULATION_'));
    const caveats = (effect.guards || []).filter((g) => !g.code.startsWith('POPULATION_'));

    return (
      <div className="effect-panel" data-testid="cr-effect-panel">
        <h4>
          Effect if approved
          <span className="effect-widens">Widens access</span>
        </h4>

        {/* The headline. Suppressed — not zeroed — when a guard says the count cannot be trusted. */}
        {blocking.length ? (
          <Alert severity="warning" data-testid="cr-effect-unmeasured">
            <strong>Not measured.</strong> {blocking.map((g) => g.message).join(' ')}
          </Alert>
        ) : (
          <div className="effect-headline" data-testid="cr-effect-headline">
            <strong>{effect.gaining}</strong>
            {` ${effect.gaining === 1 ? 'person gains' : 'people gain'} visibility.`}
            {` This table moves from ${effect.visibleNow} of ${effect.population}`}
            {effect.visibleNowNames && effect.visibleNowNames.length ?
              ` (${effect.visibleNowNames.join(', ')})` : ''}
            {` to ${effect.visibleAfter}.`}
          </div>
        )}

        {caveats.map((g) => (
          <Alert key={g.code} severity="info" className="effect-caveat" data-testid="cr-effect-caveat">
            {g.message}
          </Alert>
        ))}

        <dl className="effect-facts">
          <dt>Appears in</dt>
          <dd>{APPEARS_IN}</dd>

          <dt>PII columns</dt>
          {/* Declassifying a table with live PII is a different decision from one that has been
              purged, so the count is stated rather than left for the approver to go and check. */}
          <dd data-testid="cr-effect-pii">
            {effect.piiColumns ?
              `${effect.piiColumns} still flagged as PII` :
              'None remaining'}
          </dd>

          <dt>Reversible</dt>
          {/* internal → confidential needs no approval — restricting is monotone-safe (§7.1). */}
          <dd>Yes — reclassifying as Confidential needs no approval</dd>
        </dl>

        {/* §14.3. Until the flip the incumbent guards are authoritative and classification changes
            nothing, so this is a prediction about enforcement rather than about tomorrow. Saying so
            keeps the number from being read as an immediate consequence. */}
        {!effect.enforcing && (
          <div className="effect-shadow-note" data-testid="cr-effect-shadow">
            Enforcement is not switched on yet, so this is what approving will mean at the flip —
            not a change anyone sees today.
          </div>
        )}
      </div>
    );
  }
}

export default DeclassificationEffect;
